import type { Placement } from '@popperjs/core';
import { useMemo, type CSSProperties, type ReactNode } from 'react';
import type { Modifier } from 'react-popper';
import type { DefaultTheme } from 'styled-components';
import { useTheme } from 'styled-components';
import { getButtonHorizontalSpacing, getButtonVerticalSpacing } from '../../Button/styles';
import { Tooltip } from '../../Tooltip';
import { FormControlSizes } from '../types';

const SEARCH_SELECT_ITEM_TOOLTIP_TEST_ID = 'search-select-item-tooltip';
const SEARCH_SELECT_ITEM_TOOLTIP_DELAY = 300;

export function shouldSkipSearchSelectItemTooltip(tooltip: ReactNode): boolean {
  return tooltip == null || tooltip === false || tooltip === '';
}

function getSearchSelectItemTooltipRowPadding(
  theme: DefaultTheme,
  size: FormControlSizes = FormControlSizes.Default
): string {
  const verticalSpacing = getButtonVerticalSpacing(theme, size);
  const horizontalSpacing = getButtonHorizontalSpacing(theme, size);

  return `${verticalSpacing}px ${horizontalSpacing}px`;
}

function getSearchSelectItemTooltipTargetStyle(padding: string): CSSProperties {
  return {
    // Position on the <li> itself (react-window gives each row position: absolute + fixed height).
    // A wrapper with height: 100% only sizes to the label text (~23px), so inset must be on the
    // popover target directly to cover the full 40px row including padding below the label.
    position: 'absolute',
    inset: 0,
    // PopoverTarget defaults to height: fit-content, which prevents inset from filling the row.
    height: 'auto',
    display: 'flex',
    alignItems: 'center',
    boxSizing: 'border-box',
    padding,
  };
}

export function wrapSearchSelectResultWithTooltip(
  result: ReactNode,
  tooltip: ReactNode,
  { placement = 'left-start', size = FormControlSizes.Default }: { placement?: Placement; size?: FormControlSizes } = {}
): ReactNode {
  if (shouldSkipSearchSelectItemTooltip(tooltip)) {
    return result;
  }

  return (
    <SearchSelectItemTooltip placement={placement} size={size} tooltip={tooltip}>
      {result}
    </SearchSelectItemTooltip>
  );
}

function SearchSelectItemTooltip({
  tooltip,
  placement,
  size,
  children,
}: {
  tooltip: ReactNode;
  placement: Placement;
  size: FormControlSizes;
  children: ReactNode;
}) {
  const theme = useTheme();
  const { spacing8 } = theme;
  const rowPadding = useMemo(() => getSearchSelectItemTooltipRowPadding(theme, size), [size, theme]);
  const targetStyle = useMemo(() => getSearchSelectItemTooltipTargetStyle(rowPadding), [rowPadding]);
  const modifiers = useMemo(
    (): Modifier<string, object>[] => [
      {
        name: 'offset',
        options: {
          offset: [0, spacing8],
        },
      },
    ],
    [spacing8]
  );

  return (
    <Tooltip
      tooltip={tooltip}
      placement={placement}
      delay={SEARCH_SELECT_ITEM_TOOLTIP_DELAY}
      interactive={false}
      modifiers={modifiers}
      // Highlight changes in the virtualized list remount row tooltips and drop the hover-open timer.
      resumeHoverOnTargetMount
      targetStyle={targetStyle}
      tooltipTestID={SEARCH_SELECT_ITEM_TOOLTIP_TEST_ID}
    >
      {children}
    </Tooltip>
  );
}
